import React, { useState } from 'react';
import { Loader2, PauseCircle, ChevronDown, ChevronRight } from 'lucide-react';

export const ExecutionModalTask = ({
  workOrder,
  task,
  formData,
  handleChange,
  formErrors,
  localIsSubmitting,
  onSaveDraft,
  isCompleted,
  isPaused
}) => {
  const [showPauseReasons, setShowPauseReasons] = useState(false);

  const dateFormat = (string) => {
    if (!string) return 'N/A';
    const date = string.split('T')[0];
    const clock = string.split('T')[1];
    const [year, month, day] = date.split('-');
    const [hour, minute] = clock.split(':');
    return `${day}/${month}/${year} ${hour}:${minute}`;
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">
        Basic Information
      </h3>

      {/* Info cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-700/50 rounded-lg p-4">
          <p className="text-xs text-gray-400 mb-1">Type</p>
          <p className="text-white">
            {workOrder?.work_order_type
              ? workOrder.work_order_type.charAt(0).toUpperCase() + workOrder.work_order_type.slice(1)
              : 'N/A'}
          </p>
        </div>
        <div className="bg-gray-700/50 rounded-lg p-4">
          <p className="text-xs text-gray-400 mb-1">Scheduled date</p>
          <p className="text-white">{dateFormat(workOrder?.scheduled_date)}</p>
        </div>
        <div className="bg-gray-700/50 rounded-lg p-4">
          <p className="text-xs text-gray-400 mb-1">Priority</p>
          <p className={`font-medium ${workOrder?.priority?.toLowerCase() === 'critical' ? 'text-red-300' :
            workOrder?.priority?.toLowerCase() === 'high' ? 'text-orange-300' :
              workOrder?.priority?.toLowerCase() === 'medium' ? 'text-yellow-300' :
                'text-green-300'
            }`}>
            {workOrder?.priority
              ? workOrder.priority.charAt(0).toUpperCase() + workOrder.priority.slice(1)
              : 'N/A'}
          </p>
        </div>
      </div>

      {/* Task description (read-only) */}
      <div className="bg-gray-700/50 rounded-lg p-4">
        <p className="text-xs text-gray-400 mb-1">Task description</p>
        <p className="text-white text-sm whitespace-pre-wrap break-words">{task?.description || 'N/A'}</p>
      </div>



      {/* Observations */}
      <div className=" border-gray-700">
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Observations <span className="text-red-400">*</span>
        </label>
        <textarea
          name="observations"
          value={formData}
          onChange={handleChange}
          className={`w-full px-4 py-2 min-h-[300px] rounded-lg border overflow-visible border-gray-600 bg-gray-700 text-white ${formErrors.observations ? 'border-red-500' : 'border-gray-600'} placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y`}
          placeholder="Observations"
          disabled={localIsSubmitting || isCompleted}
        />
        {formErrors.observations && (
          <p className="mt-1 text-sm text-red-400">{formErrors.observations}</p>
        )}
        {formErrors.observations_success && (
          <p className="mt-1 text-sm text-green-400">{formErrors.observations_success}</p>
        )}
      </div>

      {/* Pause reasons if any */}
      {task?.pause_reasons && task.pause_reasons.length > 0 && (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => setShowPauseReasons(!showPauseReasons)}
            className="flex items-center gap-1.5 text-xs text-gray-400 font-medium hover:text-gray-200 transition-colors cursor-pointer"
          >
            {showPauseReasons ? (
              <ChevronDown className="w-3.5 h-3.5" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5" />
            )}
            Pause reasons
          </button>
          {showPauseReasons && (
            <div className="flex gap-2.5 flex-wrap animate-fade-in">
              {task.pause_reasons.map((reason, index) => (
                <div
                  key={index}
                  className="flex items-start gap-2.5 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-amber-200 text-sm"
                >
                  <PauseCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                  <div className="font-medium pre-whitespace ">
                    <p className="text-xs font-normal text-yellow-500/90">{reason.trim().split(']')[0].split(',')[0].split(' ')[2] + ' ' + reason.trim().split(']')[0].split(',')[1]}</p>
                    <p>{reason.trim().split(']')[1]}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={onSaveDraft}
          className="px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-gray-300 text-sm font-medium hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
          disabled={localIsSubmitting || isPaused || isCompleted}
        >
          {localIsSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
          Save Draft
        </button>
      </div>
    </div>
  );
};
